/**
 * Error codes parsed from monday API responses
 */
export enum MondayErrorCodes {
  /**
   * Not found could mean multiple things:
   * 1. Permantently deleted
   * 2. No permission to access
   * 3. Belongs to a different account
   */
  NOT_FOUND = "not_found",

  /**
   * Archival state returned directly from the API
   */
  ARCHIVED = "archived",
  BOARD_ARCHIVED = "board_archived",

  /**
   * Deleted state returned directly from the API
   */
  DELETED = "deleted",
  BOARD_DELETED = "board_deleted",

  /**
   * Integration was created with a different account.
   * (This is only possible for legacy integrations)
   */
  ACCOUNT_MISMATCH = "account_mismatch",

  /**
   * Code indicating that this user is a guest and needs to login.
   * (This is usually returned on the frontend)
   */
  LOGIN_REQUIRED = "login_required",

  /**
   * Unknown error returned from the API
   */
  UNKNOWN_ERROR = "unknown_error",
}

export type MondayItemError = {
  id: string;
  error: MondayErrorCodes;
  url?: string;
};

/**
 * Raw Monday item structure as returned from the Monday API
 * Uses snake_case field names as per GraphQL response
 */
export interface RawMondayItem {
  id: string;
  name: string;
  state: "archived" | "deleted" | "active";
  created_at: string;
  group: { id: string; title: string; color?: string };
  board?: {
    url: string;
    name: string;
    id: string;
    state: "archived" | "deleted" | "active";
    columns: Array<{
      id: string;
      type: string;
      title: string;
      settings_str: string;
    }>;
  };
  column_values: Array<{
    id: string;
    type: string;
    value: string;
    text?: string;
    display_value?: string;
  }>;
  assets: Array<{ id: string; public_url: string; file_extension: string }>;
  updates: Array<{
    id: string;
    creator?: { name: string; photo_thumb: string };
    created_at: string;
    text_body: string;
  }>;
  url: string;
  subitems: Array<{ id: string }>;
  parent_item?: {
    name: string;
    board: { name: string };
  };
}

/**
 * Mapped Monday item structure for the frontend
 * Uses camelCase field names and simplified structure
 */
export interface MondayItemData {
  id: string;
  name: string;
  createdAt: string;
  group: { id: string; title: string; color?: string };
  board?: { name: string; id: string };
  updates: Array<{
    id: string;
    userName: string;
    userURL: string;
    createdAt: string;
    text: string;
  }>;
  columnValues: Array<{
    id: string;
    value: unknown;
    type: string;
    title: string;
  }>;
  url: string;
  parent_item?: {
    name: string;
    board: { name: string };
  };
  subitems_ids: string[];
}
